import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  LineChart,
  PieChart,
  Globe,
  Clock,
  AlertTriangle,
  CheckCircle,
  Info,
  Zap,
  Filter,
  Search,
  Calendar,
  Users,
  BookOpen,
  Target,
  Award,
  MapPin,
  DollarSign,
  Percent
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface TrendData {
  id: string;
  university: string;
  category: 'acceptance-rate' | 'test-scores' | 'gpa' | 'applications' | 'tuition' | 'financial-aid';
  metric: string;
  currentValue: number;
  previousValue: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
  timeframe: string;
  significance: 'high' | 'medium' | 'low';
  lastUpdated: string;
  source: string;
}

interface LiveUpdate {
  id: string;
  university: string;
  update: string;
  timestamp: string;
  type: 'policy-change' | 'deadline-update' | 'requirement-change' | 'news' | 'alert';
  priority: 'critical' | 'high' | 'medium' | 'low';
  source: string;
}

interface AdmissionAlert {
  id: string;
  title: string;
  description: string;
  affectedUniversities: string[];
  category: 'deadline' | 'requirement' | 'policy' | 'test-optional' | 'financial-aid';
  urgency: 'critical' | 'high' | 'medium' | 'low';
  timestamp: string;
  actionRequired: boolean;
}

const trendData: TrendData[] = [
  {
    id: '1',
    university: 'Harvard University',
    category: 'acceptance-rate',
    metric: 'Acceptance Rate',
    currentValue: 3.4,
    previousValue: 3.8,
    changePercent: -10.5,
    trend: 'down',
    timeframe: 'vs. 2023',
    significance: 'high',
    lastUpdated: '2 hours ago',
    source: 'Harvard Admissions Office'
  },
  {
    id: '2',
    university: 'Stanford University',
    category: 'applications',
    metric: 'Applications Received',
    currentValue: 55471,
    previousValue: 52857,
    changePercent: 4.9,
    trend: 'up',
    timeframe: 'vs. 2023',
    significance: 'high',
    lastUpdated: '4 hours ago',
    source: 'Stanford News'
  },
  {
    id: '3',
    university: 'MIT',
    category: 'test-scores',
    metric: 'Average SAT Score',
    currentValue: 1570,
    previousValue: 1565,
    changePercent: 0.3,
    trend: 'up',
    timeframe: 'vs. 2023',
    significance: 'medium',
    lastUpdated: '6 hours ago',
    source: 'MIT Admissions'
  },
  {
    id: '4',
    university: 'UC Berkeley',
    category: 'tuition',
    metric: 'In-State Tuition',
    currentValue: 14826,
    previousValue: 14312,
    changePercent: 3.6,
    trend: 'up',
    timeframe: 'vs. 2023',
    significance: 'medium',
    lastUpdated: '1 day ago',
    source: 'UC System'
  },
  {
    id: '5',
    university: 'Yale University',
    category: 'financial-aid',
    metric: 'Average Financial Aid',
    currentValue: 62000,
    previousValue: 58000,
    changePercent: 6.9,
    trend: 'up',
    timeframe: 'vs. 2023',
    significance: 'high',
    lastUpdated: '8 hours ago',
    source: 'Yale Financial Aid Office'
  }
];

const liveUpdates: LiveUpdate[] = [
  {
    id: '1',
    university: 'Stanford University',
    update: 'Extended early action deadline to November 15th due to technical issues with application portal',
    timestamp: '1 hour ago',
    type: 'deadline-update',
    priority: 'critical',
    source: 'Stanford Admissions'
  },
  {
    id: '2',
    university: 'MIT',
    update: 'Announced new need-blind admissions policy for international students starting fall 2025',
    timestamp: '3 hours ago',
    type: 'policy-change',
    priority: 'high',
    source: 'MIT News'
  },
  {
    id: '3',
    university: 'Harvard University',
    update: 'Released updated essay prompts for 2025 application cycle',
    timestamp: '5 hours ago',
    type: 'requirement-change',
    priority: 'high',
    source: 'Harvard Admissions'
  },
  {
    id: '4',
    university: 'UC System',
    update: 'All UC schools now permanently test-optional for California residents',
    timestamp: '1 day ago',
    type: 'policy-change',
    priority: 'medium',
    source: 'UC System Office'
  }
];

const admissionAlerts: AdmissionAlert[] = [
  {
    id: '1',
    title: 'Early Decision Deadlines Approaching',
    description: 'Multiple top-tier universities have early decision deadlines within the next 2 weeks',
    affectedUniversities: ['Harvard', 'Yale', 'Princeton', 'Columbia', 'Brown'],
    category: 'deadline',
    urgency: 'critical',
    timestamp: '30 minutes ago',
    actionRequired: true
  },
  {
    id: '2',
    title: 'Test-Optional Policies Extended',
    description: 'Several universities have extended their test-optional policies through 2026',
    affectedUniversities: ['MIT', 'Caltech', 'University of Chicago'],
    category: 'test-optional',
    urgency: 'medium',
    timestamp: '2 hours ago',
    actionRequired: false
  },
  {
    id: '3',
    title: 'Financial Aid Application Deadlines',
    description: 'CSS Profile and FAFSA deadlines are approaching for priority financial aid consideration',
    affectedUniversities: ['All participating schools'],
    category: 'financial-aid',
    urgency: 'high',
    timestamp: '4 hours ago',
    actionRequired: true
  }
];

export const AdmissionTrendsTracker: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedView, setSelectedView] = useState<'trends' | 'updates' | 'alerts'>('trends');
  const [isLive, setIsLive] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    if (isLive) {
      const interval = setInterval(() => {
        setLastRefresh(new Date());
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [isLive]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="text-green-500" size={16} />;
      case 'down': return <TrendingDown className="text-red-500" size={16} />;
      default: return <Activity className="text-blue-500" size={16} />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-blue-600';
    }
  };

  const getSignificanceColor = (significance: string) => {
    switch (significance) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      default: return 'bg-blue-500 text-white';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'acceptance-rate': return Percent;
      case 'test-scores': return Target;
      case 'gpa': return BookOpen;
      case 'applications': return Users;
      case 'tuition': return DollarSign;
      case 'financial-aid': return Award;
      default: return BarChart3;
    }
  };

  const filteredTrends = selectedCategory === 'all'
    ? trendData
    : trendData.filter(trend => trend.category === selectedCategory);

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <Activity className="w-12 h-12" />
              <div>
                <h2 className="text-3xl font-bold">Live Admission Trends</h2>
                <p className="text-primary-100 text-lg">
                  Real-time tracking of college admission trends and updates
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
                <span className="text-sm text-primary-100">
                  {isLive ? 'Live Updates' : 'Paused'}
                </span>
              </div>
              <div className="text-sm text-primary-100">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </div>
            </div>
          </div>
          <div className="text-right">
            <Button
              variant="secondary"
              onClick={() => setIsLive(!isLive)}
              className="bg-white text-primary-600 hover:bg-gray-50"
            >
              <Zap size={16} className="mr-2" />
              {isLive ? 'Pause' : 'Resume'} Live Updates
            </Button>
          </div>
        </div>
      </Card>

      {/* Navigation and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3">
          <div className="flex border-b border-gray-200 mb-6">
            {[
              { id: 'trends', label: 'Trends', icon: TrendingUp },
              { id: 'updates', label: 'Live Updates', icon: Globe },
              { id: 'alerts', label: 'Alerts', icon: AlertTriangle }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setSelectedView(id as any)}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  selectedView === id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={16} />
                <span>{label}</span>
                {id === 'updates' && (
                  <Badge variant="primary" size="sm">
                    {liveUpdates.length}
                  </Badge>
                )}
                {id === 'alerts' && admissionAlerts.filter(a => a.actionRequired).length > 0 && (
                  <Badge variant="warning" size="sm">
                    {admissionAlerts.filter(a => a.actionRequired).length}
                  </Badge>
                )}
              </button>
            ))}
          </div>

          {selectedView === 'trends' && (
            <div className="space-y-4">
              {/* Category Filter */}
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">Filter by category:</span>
                <div className="flex flex-wrap gap-2">
                  {['all', 'acceptance-rate', 'test-scores', 'gpa', 'applications', 'tuition', 'financial-aid'].map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === category
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {category === 'all' ? 'All' : category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>

              {/* Trends Data */}
              <div className="space-y-4">
                {filteredTrends.map((trend, index) => {
                  const CategoryIcon = getCategoryIcon(trend.category);
                  return (
                    <motion.div
                      key={trend.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border border-gray-200 rounded-xl p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                            <CategoryIcon className="text-primary-600" size={20} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-lg font-semibold text-gray-900">{trend.university}</h4>
                              <Badge className={getSignificanceColor(trend.significance)} size="sm">
                                {trend.significance} impact
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{trend.metric}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span>Source: {trend.source}</span>
                              <span>•</span>
                              <span>{trend.lastUpdated}</span>
                              <span>•</span>
                              <span>{trend.timeframe}</span>
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="flex items-center space-x-2 mb-2">
                            {getTrendIcon(trend.trend)}
                            <span className={`text-2xl font-bold ${getTrendColor(trend.trend)}`}>
                              {trend.category === 'acceptance-rate' || trend.category === 'financial-aid'
                                ? `${trend.currentValue}${trend.category === 'acceptance-rate' ? '%' : ''}`
                                : trend.currentValue.toLocaleString()}
                            </span>
                          </div>
                          <div className={`text-sm font-semibold ${getTrendColor(trend.trend)}`}>
                            {trend.changePercent > 0 ? '+' : ''}{trend.changePercent}%
                          </div>
                          <div className="text-xs text-gray-500">
                            vs. {trend.previousValue.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {selectedView === 'updates' && (
            <div className="space-y-4">
              {liveUpdates.map((update, index) => (
                <motion.div
                  key={update.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`border-l-4 pl-6 py-4 ${getPriorityColor(update.priority)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{update.university}</h4>
                    <div className="flex items-center space-x-2">
                      <Badge className={getPriorityColor(update.priority)} size="sm">
                        {update.priority}
                      </Badge>
                      <span className="text-xs text-gray-500">{update.timestamp}</span>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-2">{update.update}</p>
                  <div className="text-xs text-gray-500">
                    Source: {update.source} • Type: {update.type.replace('-', ' ')}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {selectedView === 'alerts' && (
            <div className="space-y-4">
              {admissionAlerts.map((alert, index) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 rounded-xl p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3">
                      <div className={`w-3 h-3 rounded-full mt-2 ${
                        alert.urgency === 'critical' ? 'bg-red-500' :
                        alert.urgency === 'high' ? 'bg-orange-500' :
                        alert.urgency === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`} />
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">{alert.title}</h4>
                        <p className="text-gray-700 mb-3">{alert.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                          <span>{alert.timestamp}</span>
                          <span>•</span>
                          <span>{alert.affectedUniversities.length} universities affected</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {alert.affectedUniversities.slice(0, 3).map((uni, idx) => (
                            <Badge key={idx} variant="secondary" size="sm">{uni}</Badge>
                          ))}
                          {alert.affectedUniversities.length > 3 && (
                            <Badge variant="secondary" size="sm">
                              +{alert.affectedUniversities.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge className={getUrgencyColor(alert.urgency)} size="sm">
                      {alert.urgency}
                    </Badge>
                  </div>

                  {alert.actionRequired && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertTriangle className="text-yellow-600" size={16} />
                        <span className="font-semibold text-yellow-800">Action Required</span>
                      </div>
                      <p className="text-sm text-yellow-700">
                        This alert requires immediate attention. Review your application timeline and make necessary adjustments.
                      </p>
                      <div className="mt-3">
                        <Button size="sm" variant="outline" className="border-yellow-300 text-yellow-700">
                          Review Impact
                        </Button>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </Card>

        {/* Quick Stats Sidebar */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Stats</h3>
          <div className="space-y-6">
            <div>
              <div className="text-2xl font-bold text-primary-600 mb-1">
                {trendData.filter(t => t.trend === 'down' && t.category === 'acceptance-rate').length}
              </div>
              <div className="text-sm text-gray-600">Schools with decreasing acceptance rates</div>
            </div>

            <div>
              <div className="text-2xl font-bold text-green-600 mb-1">
                {trendData.filter(t => t.trend === 'up' && t.category === 'financial-aid').length}
              </div>
              <div className="text-sm text-gray-600">Schools increasing financial aid</div>
            </div>

            <div>
              <div className="text-2xl font-bold text-orange-600 mb-1">
                {liveUpdates.filter(u => u.priority === 'critical').length}
              </div>
              <div className="text-sm text-gray-600">Critical updates today</div>
            </div>

            <div>
              <div className="text-2xl font-bold text-red-600 mb-1">
                {admissionAlerts.filter(a => a.actionRequired).length}
              </div>
              <div className="text-sm text-gray-600">Alerts requiring action</div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-4">Data Sources</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div>• University admissions offices</div>
              <div>• Official announcements</div>
              <div>• College board data</div>
              <div>• News publications</div>
              <div>• Government databases</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};